import type {MilestonePickerMilestone} from '@github-ui/item-picker-milestone'
import {DOCUMENT_FIELDS_PATH} from '@github-ui/optimistic-connection'
import {
  createConnectionMutationId,
  dispatchConnectionMutation,
} from '@github-ui/optimistic-connection/connectionMutationBridge'
import {commitMutation, graphql} from 'react-relay'
import type {Environment} from 'relay-runtime'

import type {
  updateIssueMilestoneRelayMutation,
  updateIssueMilestoneRelayMutation$data,
} from './__generated__/updateIssueMilestoneRelayMutation.graphql'

type DashboardMilestone = Pick<MilestonePickerMilestone, 'title' | 'url'>

function milestoneFields(milestone: DashboardMilestone | null): {milestone: DashboardMilestone | null} {
  return {milestone: milestone ? {title: milestone.title, url: milestone.url} : null}
}

export function commitUpdateIssueMilestoneMutation({
  environment,
  input: {issueId, milestone, clientMutationId},
  onError,
  onCompleted,
}: {
  environment: Environment
  input: {issueId: string; milestone: MilestonePickerMilestone | null; clientMutationId?: string}
  onError?: (error: Error) => void
  onCompleted?: (response: updateIssueMilestoneRelayMutation$data) => void
}) {
  const mutationId = createConnectionMutationId()
  dispatchConnectionMutation({
    phase: 'optimistic',
    mutationId,
    ownerId: issueId,
    connectionPath: DOCUMENT_FIELDS_PATH,
    operation: {type: 'setFields', id: issueId, fields: milestoneFields(milestone)},
  })

  const fail = (error: Error) => dispatchConnectionMutation({phase: 'error', mutationId, error})

  let disposable
  try {
    disposable = commitMutation<updateIssueMilestoneRelayMutation>(environment, {
      // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
      mutation: graphql`
        mutation updateIssueMilestoneRelayMutation($input: UpdateIssueInput!) @raw_response_type {
          updateIssue(input: $input) {
            issue {
              id
              milestone {
                id
                title
                closed
                dueOn
                progressPercentage
                url
                closedAt
              }
            }
          }
        }
      `,
      variables: {input: {id: issueId, milestoneId: milestone ? milestone.id : null, clientMutationId}},
      optimisticResponse: {
        updateIssue: {
          issue: {
            id: issueId,
            milestone: milestone
              ? {
                  id: milestone.id,
                  title: milestone.title,
                  closed: milestone.closed,
                  dueOn: milestone.dueOn,
                  progressPercentage: milestone.progressPercentage,
                  url: milestone.url,
                  closedAt: milestone.closedAt,
                }
              : null,
          },
        },
      },
      onError: error => {
        fail(error)
        onError?.(error)
      },
      onUnsubscribe: () => fail(new Error('Updating the issue milestone was cancelled')),
      onCompleted: (response, errors) => {
        const issue = response.updateIssue?.issue
        const confirmedMilestone = issue?.milestone
        const confirmedFields =
          milestone && confirmedMilestone?.id === milestone.id
            ? milestoneFields(confirmedMilestone)
            : !milestone && confirmedMilestone === null
              ? milestoneFields(null)
              : undefined
        if (!issue || !confirmedFields) {
          const error = new Error(errors?.[0]?.message ?? 'Failed to update issue milestone')
          fail(error)
          onError?.(error)
          return
        }

        dispatchConnectionMutation({
          phase: 'completed',
          mutationId,
          ownerId: issue.id,
          connectionPath: DOCUMENT_FIELDS_PATH,
          operation: {type: 'setFields', id: issue.id, fields: confirmedFields},
        })
        onCompleted?.(response)
      },
    })
  } catch (error) {
    fail(error instanceof Error ? error : new Error('Failed to update issue milestone'))
    throw error
  }

  return disposable
}
